import {Button, Modal, NumberInput, Select, Stack, Textarea, TextInput} from "@mantine/core";
import {useForm} from "@mantine/form";
import {CreatePayload, DeviceApi} from "../../api/device/DeviceApi";
import {useEffect, useState} from "react";
import {showNotification} from "@mantine/notifications";
import {Company, CompanyAPI} from "../../api/company/CompanyAPI";

export type CreateDeviceDialogAttrs = {
    isOpen: boolean;
    onClose: () => void;
    reload: () => void;
}

function CreateDeviceDialog(attrs: CreateDeviceDialogAttrs) {
    const [companyList, setCompanyList] = useState<Company[]>();
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            unit: '',
            name: '',
            description: '',
            minAllowableTemp: 2.0,
            maxAllowableTemp: 8.0,
            serialNumber: '',
            clientVisibleName: '',
            clientEmailAddress: '',
            clientPhoneNumber: '',
            payment: '',
            medicalTransport: ''
        },

        validate: {
            unit: (value) => (value.length < 2 ? 'Przynajmniej dwa znaki.' : null),
            name: (v) => (v.length < 2 ? "Przynajmniej dwa znaki." : null)
        }
    });

    const onCreated = () => {
        attrs.onClose();
    };

    const onCreateError = () => {
        showNotification(
            {
                title: 'Nie udało się zapisać urządzenia',
                message: 'Proszę upewnić się, że jednostka nie jest zajęta.',
                color: 'red'
            }
        );

        form.setFieldError('unit', 'Proszę upewnić się, że jednostka nie jest zajęta.');
    }

    const onSuccess = () => {
        showNotification(
            {
                title: 'Nowe urządzenie!',
                message: 'Utworzyłeś nowe urządzenie!',
                color: 'green'
            }
        );
    }

    const create = (values: any) => {
        setSaving(true);

        const p: CreatePayload = {
            ...values,
            clientVisibleName: values.clientVisibleName === '' ? undefined : values.clientVisibleName,
            clientEmailAddress: values.clientEmailAddress === '' ? undefined : values.clientEmailAddress,
            clientPhoneNumber: values.clientPhoneNumber === '' ? undefined : values.clientPhoneNumber,
            medicalTransport: values.medicalTransport === '' ? undefined : values.medicalTransport,
            payment: values.payment === '' ? null : values.payment,
            companyId: companyId ?? null,
        };

        DeviceApi.create(p)
            .then(onCreated)
            .then(attrs.reload)
            .then(onSuccess)
            .catch(onCreateError)
            .finally(() => setSaving(false));
    };

    useEffect(() => {
      if (attrs.isOpen) {
        CompanyAPI.listAsync().then(setCompanyList);
        return;
      }

      setCompanyList(undefined);
      setCompanyId(null);
    }, [attrs.isOpen]);

    return (
        <Modal
            title="Utwórz urządzenie"
            opened={attrs.isOpen}
            onClose={attrs.onClose}
        >

            <form onSubmit={form.onSubmit((values) => create(values))}>
                <Stack>
                    <TextInput
                        withAsterisk
                        label="Jednostka"
                        placeholder="1234"
                        variant="filled"
                        key={form.key("unit")}
                        {...form.getInputProps("unit")}
                    />

                    <TextInput
                        withAsterisk
                        label="Nazwa urządzenia"
                        placeholder="Urządzenie pomiarowe"
                        variant="filled"
                        key={form.key("name")}
                        {...form.getInputProps("name")}
                    />

                    <Textarea
                        label="Opis"
                        placeholder="Urządzenie pomiarowe"
                        resize={'vertical'}
                        minRows={3}
                        variant="filled"
                        key={form.key("description")}
                        {...form.getInputProps("description")}
                    />

                    <NumberInput
                        label="Min. dopuszczalna temperatura"
                        step={.1}
                        key={form.key("minAllowableTemp")}
                        {...form.getInputProps("minAllowableTemp")}
                    />

                    <NumberInput
                        label="Maks. dopuszczalna temperatura"
                        step={.1}
                        key={form.key('maxAllowableTemp')}
                        {...form.getInputProps("maxAllowableTemp")}
                    />

                    <TextInput
                        label="Nazwa klienta"
                        key={form.key("clientVisibleName")}
                        {...form.getInputProps("clientVisibleName")}
                    />

                    <TextInput
                        label="Adres E-mail"
                        key={form.key("clientEmailAddress")}
                        {...form.getInputProps("clientEmailAddress")}
                    />

                    <TextInput
                        label="Numer telefonu"
                        key={form.key("clientPhoneNumber")}
                        {...form.getInputProps("clientPhoneNumber")}
                    />

                    <TextInput
                        withAsterisk
                        label="Numer seryjny"
                        placeholder="Numer seryjny"
                        variant="filled"
                        key={form.key("serialNumber")}
                        {...form.getInputProps("serialNumber")}
                    />

                    <TextInput
                        label="Płatność"
                        placeholder="Czy klient płaci za urządzenie?"
                        variant="filled"
                        key={form.key("payment")}
                        {...form.getInputProps("payment")}
                    />

                    <TextInput
                        label="Transport medyczny"
                        placeholder="Transport medyczny"
                        variant="filled"
                        key={form.key("payment")}
                        {...form.getInputProps("medicalTransport")}
                    />

                    <Select
                      searchable
                      clearable
                      label="Firma"
                      value={companyId}
                      placeholder="Wybierz firmę lub pozostaw puste"
                      onChange={(t) => setCompanyId(t)}
                      data={companyList?.map(c => ({ label: c.title, value: c.id })) ?? []}
                    />

                    <Button
                        loading={saving}
                        loaderProps={{type: 'dots'}}
                        type="submit"
                    >
                        Utwórz
                    </Button>
                </Stack>
            </form>

        </Modal>
    );
}

export default CreateDeviceDialog;
